import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarWidget } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  LogIn, Clock, ListChecks, Calendar, CalendarDays, Filter,
  ChevronDown, ChevronUp, Loader2, Users, FileText, ArrowUpRight, ArrowDownRight,
  Trophy, Star, TrendingUp, Zap, BarChart3, AlertCircle, Building, PieChart, Timer, Eye,
  DollarSign, Palmtree, Target, Shield, Lock, Activity, UserX, AlertTriangle, CheckCircle2, XCircle, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILTER_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'this_month', label: 'This Month' },
  { key: 'this_year', label: 'This Year' },
];

const MONTHS = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' },
  { value: '03', label: 'March' }, { value: '04', label: 'April' },
  { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' },
  { value: '09', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

function getYearOptions() {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current; y >= 2020; y--) years.push(String(y));
  return years;
}

function formatMin(m) {
  if (!m) return '0h';
  const h = Math.floor(m / 60);
  const mins = m % 60;
  return mins > 0 ? `${h}h ${mins}m` : `${h}h`;
}

function formatMinutes(m) { return formatMin(m); }

function formatTime(isoStr) {
  if (!isoStr) return '-';
  try { return new Date(isoStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return isoStr; }
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try { return new Date(dateStr + 'T00:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' }); }
  catch { return dateStr; }
}

const SIGNAL_META = {
  check_in: { label: 'Check-In', icon: LogIn, color: 'text-blue-600' },
  check_in_on_time: { label: 'On Time', icon: ArrowUpRight, color: 'text-green-600' },
  check_in_late: { label: 'Late', icon: ArrowDownRight, color: 'text-red-500' },
  check_out: { label: 'Check-Out', icon: Clock, color: 'text-indigo-600' },
  day_planner: { label: 'Day Planner', icon: CalendarDays, color: 'text-teal-600' },
  timesheet_submitted: { label: 'Timesheet', icon: FileText, color: 'text-emerald-600' },
  task_created: { label: 'Task Created', icon: ListChecks, color: 'text-violet-600' },
  task_completed: { label: 'Completed', icon: Star, color: 'text-amber-500' },
  task_status_changed: { label: 'Status Changed', icon: TrendingUp, color: 'text-orange-500' },
  ping_used: { label: 'Ping', icon: Zap, color: 'text-pink-500' },
};

// ─── Shared Filter Bar ───────────────────────────────────────────────
function FilterBar({ activeFilter, onPreset, customDate, onDateChange, customMonth, onMonthChange, customYear, onYearChange }) {
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleCalendarSelect = (date) => {
    if (date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      onDateChange(`${y}-${m}-${d}`);
      setCalendarOpen(false);
    }
  };

  // Generate month-year options (current year + past 2 years)
  const monthYearOptions = [];
  const now = new Date();
  for (let y = now.getFullYear(); y >= 2020; y--) {
    const maxMonth = y === now.getFullYear() ? now.getMonth() + 1 : 12;
    for (let m = maxMonth; m >= 1; m--) {
      const val = `${y}-${String(m).padStart(2, '0')}`;
      const label = `${MONTHS[m - 1].label} ${y}`;
      monthYearOptions.push({ value: val, label });
    }
  }

  const selectedDate = customDate ? new Date(customDate + 'T00:00:00') : undefined;
  const dateLabel = customDate
    ? new Date(customDate + 'T00:00:00').toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Pick a date';
  const monthLabel = customMonth
    ? `${MONTHS[parseInt(customMonth.slice(5, 7), 10) - 1]?.label || ''} ${customMonth.slice(0, 4)}`
    : 'Select month';

  return (
    <Card>
      <CardContent className="py-4 px-5">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="h-4 w-4 text-gray-400" />
          {FILTER_PRESETS.map(f => (
            <Button key={f.key} size="sm" data-testid={`filter-${f.key}`}
              variant={activeFilter === f.key ? 'default' : 'outline'}
              onClick={() => onPreset(f.key)}
              className={activeFilter === f.key ? 'bg-teal-600 hover:bg-teal-700' : ''}>
              {f.label}
            </Button>
          ))}
          <div className="h-6 w-px bg-gray-200 mx-1" />

          {/* Date Picker with Calendar */}
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="filter-date"
                className={`w-[160px] justify-start text-left font-normal ${activeFilter === 'date' ? 'ring-2 ring-teal-500 border-teal-500' : ''}`}>
                <Calendar className="mr-2 h-3.5 w-3.5 text-gray-400" />
                <span className={`text-xs ${customDate ? 'text-gray-900' : 'text-gray-400'}`}>{dateLabel}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarWidget mode="single" selected={selectedDate}
                onSelect={handleCalendarSelect} initialFocus />
            </PopoverContent>
          </Popover>

          {/* Month Picker (combined month + year) */}
          <Select value={customMonth || ''} onValueChange={onMonthChange}>
            <SelectTrigger className={`h-8 w-[170px] text-xs ${activeFilter === 'month' ? 'ring-2 ring-teal-500 border-teal-500' : ''}`}
              data-testid="filter-month">
              <CalendarDays className="h-3.5 w-3.5 text-gray-400 mr-1.5 shrink-0" />
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent className="max-h-[280px]">
              {monthYearOptions.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Year Picker */}
          <Select value={customYear || ''} onValueChange={onYearChange}>
            <SelectTrigger className={`h-8 w-[90px] text-xs ${activeFilter === 'year' ? 'ring-2 ring-teal-500 border-teal-500' : ''}`}
              data-testid="filter-year">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {getYearOptions().map(y => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Component ──────────────────────────────────────────────────
export default function PlatformAnalytics() {
  const { getAuthHeaders, isSuperAdmin } = useAuth();
  const isSuper = isSuperAdmin();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('today');
  const [customDate, setCustomDate] = useState('');
  const [customMonth, setCustomMonth] = useState('');
  const [customYear, setCustomYear] = useState('');

  // Overview data
  const [overviewData, setOverviewData] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  // Scores data
  const [scoresData, setScoresData] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  // Timesheet analysis data
  const [tsAnalyticsData, setTsAnalyticsData] = useState(null);
  const [tsOfficeFilter, setTsOfficeFilter] = useState('all');
  const [tsExpandedRow, setTsExpandedRow] = useState(null);
  const [tsExpandedEmp, setTsExpandedEmp] = useState(null);

  // New analytics tabs data
  const [expenseData, setExpenseData] = useState(null);
  const [leaveData, setLeaveData] = useState(null);
  const [taskAnalysisData, setTaskAnalysisData] = useState(null);
  const [securityData, setSecurityData] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(null);

  const fetchData = useCallback(async (filterType, filterValue = null) => {
    setLoading(true);
    setError(null);
    try {
      const headers = getAuthHeaders();
      let qs = `filter_type=${filterType}`;
      if (filterValue) qs += `&filter_value=${filterValue}`;

      const fetchList = [
        fetch(`${API_URL}/api/analytics/platform?${qs}`, { headers }),
        fetch(`${API_URL}/api/analytics/workspace-scores?${qs}`, { headers }),
        fetch(`${API_URL}/api/analytics/timesheets?${qs}`, { headers }),
        fetch(`${API_URL}/api/analytics/expenses?${qs}`, { headers }),
        fetch(`${API_URL}/api/analytics/leaves?${qs}`, { headers }),
        fetch(`${API_URL}/api/analytics/tasks-analysis?${qs}`, { headers }),
      ];
      // Only super admins fetch security analytics
      if (isSuper) {
        fetchList.push(fetch(`${API_URL}/api/analytics/security?${qs}`, { headers }));
        fetchList.push(fetch(`${API_URL}/api/analytics/online-users`, { headers }));
      }

      const results = await Promise.allSettled(fetchList);

      let overviewOk = false;
      let scoresOk = false;

      // Handle platform overview
      if (results[0].status === 'fulfilled') {
        if (results[0].value.ok) {
          const data = await results[0].value.json();
          setOverviewData(data);
          overviewOk = true;
        } else {
          console.error('Platform API status:', results[0].value.status, await results[0].value.text().catch(() => ''));
        }
      } else {
        console.error('Platform fetch rejected:', results[0].reason);
      }

      // Handle workspace scores
      if (results[1].status === 'fulfilled') {
        if (results[1].value.ok) {
          const data = await results[1].value.json();
          setScoresData(data);
          scoresOk = true;
        } else {
          console.error('Scores API status:', results[1].value.status, await results[1].value.text().catch(() => ''));
        }
      } else {
        console.error('Scores fetch rejected:', results[1].reason);
      }

      // Handle timesheet analytics
      if (results[2].status === 'fulfilled' && results[2].value.ok) {
        setTsAnalyticsData(await results[2].value.json());
        if (!overviewOk && !scoresOk) overviewOk = true; // At least one succeeded
      }

      // Handle expense analytics
      if (results[3].status === 'fulfilled' && results[3].value.ok) {
        setExpenseData(await results[3].value.json());
      }

      // Handle leave analytics
      if (results[4].status === 'fulfilled' && results[4].value.ok) {
        setLeaveData(await results[4].value.json());
      }

      // Handle task analysis
      if (results[5].status === 'fulfilled' && results[5].value.ok) {
        setTaskAnalysisData(await results[5].value.json());
      }

      // Handle security analytics (super admin only, index 6)
      if (isSuper && results[6] && results[6].status === 'fulfilled' && results[6].value.ok) {
        setSecurityData(await results[6].value.json());
      }

      // Handle online users (super admin only, index 7)
      if (isSuper && results[7] && results[7].status === 'fulfilled' && results[7].value.ok) {
        setOnlineUsers(await results[7].value.json());
      }

      if (!overviewOk && !scoresOk) {
        setError('Failed to load analytics data. Please try a different filter or try again.');
      }
    } catch (err) {
      console.error('Analytics fetch failed:', err);
      setError('Failed to load analytics data. Please try again.');
    }
    finally { setLoading(false); }
  }, [getAuthHeaders, isSuper]);

  useEffect(() => { fetchData('today'); }, [fetchData]);

  const handlePreset = (key) => {
    setActiveFilter(key); setCustomDate(''); setCustomMonth(''); setCustomYear('');
    fetchData(key);
  };
  const handleDateChange = (val) => {
    setCustomDate(val); setActiveFilter('date'); setCustomMonth(''); setCustomYear('');
    if (val) fetchData('date', val);
  };
  const handleMonthChange = (val) => {
    setCustomMonth(val); setActiveFilter('month'); setCustomDate(''); setCustomYear('');
    if (val) fetchData('month', val);
  };
  const handleYearChange = (val) => {
    setCustomYear(val); setActiveFilter('year'); setCustomDate(''); setCustomMonth('');
    if (val) fetchData('year', val);
  };

  const filterLabel = activeFilter === 'today' ? 'Today'
    : activeFilter === 'this_month' ? 'This Month'
    : activeFilter === 'this_year' ? 'This Year'
    : activeFilter === 'date' ? customDate
    : activeFilter === 'month' ? customMonth
    : customYear;

  return (
    <div className="space-y-6" data-testid="platform-analytics">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="analytics-title">
          {isSuper ? 'Platform Analytics' : 'Office Analytics'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isSuper ? 'Monitor activity and engagement across all offices' : 'Monitor activity and engagement for your office'}
        </p>
      </div>

      <FilterBar activeFilter={activeFilter} onPreset={handlePreset}
        customDate={customDate} onDateChange={handleDateChange}
        customMonth={customMonth} onMonthChange={handleMonthChange}
        customYear={customYear} onYearChange={handleYearChange} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start border-b rounded-none bg-transparent p-0 h-auto overflow-x-auto flex-nowrap">
          <TabsTrigger value="overview" data-testid="tab-overview"
            className="data-[state=active]:border-b-2 data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 rounded-none pb-2.5 px-5">
            <BarChart3 className="h-4 w-4 mr-2" /> Activity Overview
          </TabsTrigger>
          <TabsTrigger value="scores" data-testid="tab-scores"
            className="data-[state=active]:border-b-2 data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 rounded-none pb-2.5 px-5">
            <Trophy className="h-4 w-4 mr-2" /> Workspace Scores
          </TabsTrigger>
          <TabsTrigger value="timesheets" data-testid="tab-timesheets"
            className="data-[state=active]:border-b-2 data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 rounded-none pb-2.5 px-5">
            <PieChart className="h-4 w-4 mr-2" /> Timesheets
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses"
            className="data-[state=active]:border-b-2 data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 rounded-none pb-2.5 px-5">
            <DollarSign className="h-4 w-4 mr-2" /> Expenses
          </TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-leaves"
            className="data-[state=active]:border-b-2 data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 rounded-none pb-2.5 px-5">
            <Palmtree className="h-4 w-4 mr-2" /> Leave
          </TabsTrigger>
          <TabsTrigger value="tasks-analysis" data-testid="tab-tasks-analysis"
            className="data-[state=active]:border-b-2 data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 rounded-none pb-2.5 px-5">
            <Target className="h-4 w-4 mr-2" /> Tasks
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security"
            className={`data-[state=active]:border-b-2 data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 rounded-none pb-2.5 px-5 ${!isSuper ? 'hidden' : ''}`}>
            <Shield className="h-4 w-4 mr-2" /> Security
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
            <p className="text-sm">{error}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => fetchData(activeFilter, activeFilter === 'date' ? customDate : activeFilter === 'month' ? customMonth : activeFilter === 'year' ? customYear : null)}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            <TabsContent value="overview" className="mt-6 space-y-5">
              <OverviewTab data={overviewData} filterLabel={filterLabel}
                expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
            </TabsContent>
            <TabsContent value="scores" className="mt-6 space-y-5">
              <ScoresTab data={scoresData} filterLabel={filterLabel}
                expandedEmployee={expandedEmployee} setExpandedEmployee={setExpandedEmployee} />
            </TabsContent>
            <TabsContent value="timesheets" className="mt-6 space-y-5">
              <TimesheetAnalysisTab data={tsAnalyticsData} filterLabel={filterLabel}
                officeFilter={tsOfficeFilter} setOfficeFilter={setTsOfficeFilter}
                expandedRow={tsExpandedRow} setExpandedRow={setTsExpandedRow}
                expandedEmp={tsExpandedEmp} setExpandedEmp={setTsExpandedEmp}
                getAuthHeaders={getAuthHeaders} activeFilter={activeFilter}
                customDate={customDate} customMonth={customMonth} customYear={customYear}
                isSuper={isSuper} />
            </TabsContent>
            <TabsContent value="expenses" className="mt-6 space-y-5">
              <ExpensesTab data={expenseData} filterLabel={filterLabel} />
            </TabsContent>
            <TabsContent value="leaves" className="mt-6 space-y-5">
              <LeavesTab data={leaveData} filterLabel={filterLabel} />
            </TabsContent>
            <TabsContent value="tasks-analysis" className="mt-6 space-y-5">
              <TasksAnalysisTab data={taskAnalysisData} filterLabel={filterLabel} />
            </TabsContent>
            <TabsContent value="security" className="mt-6 space-y-5">
              <SecurityTab data={securityData} filterLabel={filterLabel} onlineUsers={onlineUsers} getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}

// ─── Overview Tab ────────────────────────────────────────────────────
function OverviewTab({ data, filterLabel, expandedSection, setExpandedSection }) {
  const ci = data?.check_ins || { count: 0, items: [] };
  const ts = data?.timesheets || { count: 0, items: [] };
  const tk = data?.tasks_created || { count: 0, items: [] };
  const toggle = (s) => setExpandedSection(prev => prev === s ? null : s);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatCard icon={LogIn} label="Check-Ins" count={ci.count} color="blue"
          subtitle={filterLabel} onClick={() => toggle('checkins')}
          expanded={expandedSection === 'checkins'} testId="stat-checkins" />
        <StatCard icon={FileText} label="Timesheets Logged" count={ts.count} color="emerald"
          subtitle={filterLabel} onClick={() => toggle('timesheets')}
          expanded={expandedSection === 'timesheets'} testId="stat-timesheets" />
        <StatCard icon={ListChecks} label="Tasks Created" count={tk.count} color="violet"
          subtitle={filterLabel} onClick={() => toggle('tasks')}
          expanded={expandedSection === 'tasks'} testId="stat-tasks" />
      </div>

      {expandedSection === 'checkins' && (
        <DetailTable title="Check-In Details" color="blue" testId="checkin-table"
          columns={['Employee', 'Office', 'Date', 'Check In', 'Check Out', 'Early/Late']}
          rows={ci.items.map(i => [
            i.employee_name, i.office_name, formatDate(i.date),
            formatTime(i.check_in_time), formatTime(i.check_out_time),
            i.early_late_minutes != null ? (
              <Badge variant="outline" className={i.early_late_minutes <= 0 ? 'text-green-600 border-green-300' : 'text-red-600 border-red-300'}>
                {i.early_late_minutes <= 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                {Math.abs(i.early_late_minutes)}m {i.early_late_minutes <= 0 ? 'early' : 'late'}
              </Badge>
            ) : '-'
          ])} />
      )}

      {expandedSection === 'timesheets' && (
        <DetailTable title="Timesheet Details" color="emerald" testId="timesheet-table"
          columns={['Employee', 'Office', 'Date', 'Time Logged', 'Entries', 'Status']}
          rows={ts.items.map(i => [
            i.employee_name, i.office_name, formatDate(i.date),
            formatMinutes(i.total_minutes), i.entry_count,
            <Badge variant="outline" className={i.status === 'Logged' ? 'text-green-600 border-green-300' : 'text-amber-600 border-amber-300'}>{i.status}</Badge>
          ])} />
      )}

      {expandedSection === 'tasks' && (
        <DetailTable title="Task Details" color="violet" testId="task-table"
          columns={['Task', 'Created By', 'Office', 'Priority', 'Status', 'Type']}
          rows={tk.items.map(i => [
            <span className="font-medium max-w-[250px] truncate block">{i.title}</span>,
            i.created_by, i.office_name,
            <Badge variant="outline" className={
              i.priority === 'Urgent' ? 'text-red-600 border-red-300'
                : i.priority === 'High' ? 'text-orange-600 border-orange-300'
                : i.priority === 'Medium' ? 'text-blue-600 border-blue-300'
                : 'text-gray-600 border-gray-300'
            }>{i.priority}</Badge>,
            i.status,
            <Badge variant="secondary" className="text-xs">{i.task_type}</Badge>
          ])} />
      )}
    </>
  );
}

const CATEGORY_META = {
  'Attendance & Punctuality': { icon: LogIn, color: 'bg-blue-500', light: 'bg-blue-100 text-blue-700' },
  'Planning & Discipline': { icon: CalendarDays, color: 'bg-teal-500', light: 'bg-teal-100 text-teal-700' },
  'Task Productivity': { icon: ListChecks, color: 'bg-violet-500', light: 'bg-violet-100 text-violet-700' },
  'Collaboration': { icon: Zap, color: 'bg-pink-500', light: 'bg-pink-100 text-pink-700' },
};

// ─── Scores Tab ──────────────────────────────────────────────────────
function ScoresTab({ data, filterLabel, expandedEmployee, setExpandedEmployee }) {
  const scores = data?.scores || [];
  const weights = data?.weights || {};
  const targetScore = data?.target_score || 100;
  const categoryTargets = data?.categories || {};

  return (
    <>
      {/* Category targets overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(categoryTargets).map(([cat, target]) => {
          const meta = CATEGORY_META[cat] || {};
          const Icon = meta.icon || Star;
          return (
            <Card key={cat} className="border-l-4" style={{ borderLeftColor: meta.color?.replace('bg-', '') }}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`p-1.5 rounded ${meta.light || 'bg-gray-100'}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <span className="text-xs font-medium text-gray-600 truncate">{cat}</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{target} <span className="text-xs font-normal text-gray-400">pts</span></p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Signal weights reference */}
      <Card className="bg-gray-50/80 border-dashed">
        <CardContent className="py-3 px-5">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <span className="font-semibold text-gray-600">Points per action:</span>
            {Object.entries(weights).map(([key, pts]) => {
              const meta = SIGNAL_META[key];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <span key={key} className="flex items-center gap-1">
                  <Icon className={`h-3 w-3 ${meta.color}`} />
                  <span className="text-gray-600">{meta.label}</span>
                  <span className={`font-bold ${pts >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {pts >= 0 ? `+${pts}` : pts}
                  </span>
                </span>
              );
            })}
            <span className="text-gray-400 ml-2">Target: <strong className="text-gray-700">{targetScore}</strong> pts/month</span>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card data-testid="scores-leaderboard">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Employee Workspace Scores
            <Badge variant="secondary" className="ml-auto">{filterLabel} &middot; {scores.length} employees</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {scores.length === 0 ? (
              <div className="py-12 text-center text-gray-400">No employee data for this period</div>
            ) : scores.map((emp, idx) => (
              <div key={emp.user_id} data-testid={`score-row-${idx}`}>
                <div className="flex items-center gap-4 px-5 py-3.5 cursor-pointer hover:bg-gray-50/80 transition-colors"
                  onClick={() => setExpandedEmployee(prev => prev === emp.user_id ? null : emp.user_id)}>
                  {/* Rank */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
                    idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-gray-200 text-gray-600' : idx === 2 ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {idx + 1}
                  </div>

                  {/* Name + Office */}
                  <div className="min-w-[140px]">
                    <p className="font-medium text-sm text-gray-900">{emp.employee_name}</p>
                    <p className="text-xs text-gray-400">{emp.office_name}</p>
                  </div>

                  {/* Score bar (out of 100) */}
                  <div className="flex-1 flex items-center gap-3">
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden relative">
                      {/* Category segments */}
                      {emp.categories && (() => {
                        let offset = 0;
                        const cats = Object.entries(emp.categories);
                        return cats.map(([cat, info]) => {
                          const width = Math.max(0, (info.score / targetScore) * 100);
                          const left = offset;
                          offset += width;
                          const meta = CATEGORY_META[cat] || {};
                          return (
                            <div key={cat} className={`absolute top-0 h-full ${meta.color || 'bg-gray-400'} transition-all duration-500`}
                              style={{ left: `${left}%`, width: `${width}%` }}
                              title={`${cat}: ${info.score}/${info.target} pts`} />
                          );
                        });
                      })()}
                      {/* 100 mark */}
                      <div className="absolute top-0 right-0 h-full w-px bg-gray-300" />
                    </div>
                    <div className="min-w-[80px] text-right">
                      <span className="font-bold text-lg tabular-nums">{emp.total_score}</span>
                      <span className="text-xs text-gray-400">/{targetScore}</span>
                    </div>
                  </div>

                  {/* Percentage badge */}
                  <Badge variant="outline" className={`min-w-[50px] justify-center ${
                    emp.score_pct >= 80 ? 'text-green-600 border-green-300 bg-green-50'
                      : emp.score_pct >= 50 ? 'text-amber-600 border-amber-300 bg-amber-50'
                      : 'text-red-600 border-red-300 bg-red-50'
                  }`}>
                    {emp.score_pct}%
                  </Badge>

                  <div className="text-gray-400">
                    {expandedEmployee === emp.user_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>

                {/* Expanded breakdown */}
                {expandedEmployee === emp.user_id && (
                  <div className="px-5 pb-4 bg-gray-50/50 space-y-4">
                    {/* Category progress bars */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                      {emp.categories && Object.entries(emp.categories).map(([cat, info]) => {
                        const meta = CATEGORY_META[cat] || {};
                        const Icon = meta.icon || Star;
                        return (
                          <div key={cat} className="bg-white rounded-lg border p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className={`p-1 rounded ${meta.light || 'bg-gray-100'}`}>
                                  <Icon className="h-3.5 w-3.5" />
                                </div>
                                <span className="text-xs font-medium text-gray-700">{cat}</span>
                              </div>
                              <span className="text-sm font-bold">{info.score}<span className="text-xs text-gray-400 font-normal">/{info.target}</span></span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${meta.color || 'bg-gray-400'} transition-all`}
                                style={{ width: `${Math.min(100, info.pct)}%` }} />
                            </div>
                            <p className="text-right text-xs text-gray-400 mt-1">{info.pct}%</p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Signal breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {Object.entries(emp.breakdown).map(([key, count]) => {
                        const meta = SIGNAL_META[key];
                        if (!meta) return null;
                        const Icon = meta.icon;
                        const pts = Math.round(((weights[key] || 0) * count) * 10) / 10;
                        return (
                          <div key={key} className="bg-white rounded-lg border px-3 py-2.5 flex items-center gap-2.5">
                            <Icon className={`h-4 w-4 shrink-0 ${meta.color}`} />
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500 truncate">{meta.label}</p>
                              <div className="flex items-baseline gap-1.5">
                                <span className="font-bold text-sm">{count}</span>
                                <span className={`text-xs font-medium ${pts >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                  {pts >= 0 ? `+${pts}` : pts}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

// ─── Timesheet Category Colors ───────────────────────────────────────
const CATEGORY_COLORS = [
  { bg: 'bg-blue-500', light: 'bg-blue-100 text-blue-700', text: 'text-blue-600' },
  { bg: 'bg-emerald-500', light: 'bg-emerald-100 text-emerald-700', text: 'text-emerald-600' },
  { bg: 'bg-violet-500', light: 'bg-violet-100 text-violet-700', text: 'text-violet-600' },
  { bg: 'bg-amber-500', light: 'bg-amber-100 text-amber-700', text: 'text-amber-600' },
  { bg: 'bg-pink-500', light: 'bg-pink-100 text-pink-700', text: 'text-pink-600' },
  { bg: 'bg-teal-500', light: 'bg-teal-100 text-teal-700', text: 'text-teal-600' },
  { bg: 'bg-orange-500', light: 'bg-orange-100 text-orange-700', text: 'text-orange-600' },
  { bg: 'bg-indigo-500', light: 'bg-indigo-100 text-indigo-700', text: 'text-indigo-600' },
  { bg: 'bg-red-500', light: 'bg-red-100 text-red-700', text: 'text-red-600' },
  { bg: 'bg-gray-500', light: 'bg-gray-100 text-gray-700', text: 'text-gray-600' },
];

// ─── Timesheet Analysis Tab ──────────────────────────────────────────
function TimesheetAnalysisTab({ data, filterLabel, officeFilter, setOfficeFilter, expandedRow, setExpandedRow, expandedEmp, setExpandedEmp, getAuthHeaders, activeFilter, customDate, customMonth, customYear, isSuper }) {
  const [localData, setLocalData] = useState(data);
  const [loadingOffice, setLoadingOffice] = useState(false);

  useEffect(() => { setLocalData(data); }, [data]);

  // Re-fetch when office filter changes
  const handleOfficeChange = async (newOffice) => {
    setOfficeFilter(newOffice);
    setLoadingOffice(true);
    try {
      let qs = `filter_type=${activeFilter}`;
      const filterVal = activeFilter === 'date' ? customDate : activeFilter === 'month' ? customMonth : activeFilter === 'year' ? customYear : null;
      if (filterVal) qs += `&filter_value=${filterVal}`;
      if (newOffice && newOffice !== 'all') qs += `&office_id=${newOffice}`;

      const res = await fetch(`${API_URL}/api/analytics/timesheets?${qs}`, { headers: getAuthHeaders() });
      if (res.ok) setLocalData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingOffice(false); }
  };

  if (!localData) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;

  const s = localData.summary || {};
  const categories = localData.category_breakdown || [];
  const employees = localData.employee_breakdown || [];
  const offices = localData.offices || [];
  const timesheets = localData.timesheets || [];
  const officeBreakdown = localData.office_breakdown || [];

  return (
    <>
      {/* Office Filter — super admin only */}
      {isSuper && (
      <div className="flex items-center gap-3">
        <Building className="h-4 w-4 text-gray-400" />
        <span className="text-sm text-gray-500">Office:</span>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => handleOfficeChange('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${officeFilter === 'all' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'}`}
            data-testid="ts-office-all">
            All Offices
          </button>
          {offices.map(o => (
            <button key={o.id} onClick={() => handleOfficeChange(o.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${officeFilter === o.id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'}`}
              data-testid={`ts-office-${o.id}`}>
              {o.name}
            </button>
          ))}
          {loadingOffice && <Loader2 className="h-4 w-4 animate-spin text-teal-600" />}
        </div>
      </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <TsSummaryCard label="Timesheets" value={s.total_timesheets} icon={FileText} color="blue" />
        <TsSummaryCard label="Hours Logged" value={s.total_hours_logged} suffix="h" icon={Clock} color="emerald" />
        <TsSummaryCard label="Target Hours" value={s.total_hours_target} suffix="h" icon={TrendingUp} color="gray" />
        <TsSummaryCard label="Hours Idle" value={s.total_hours_idle} suffix="h" icon={Timer} color="red" />
        <TsSummaryCard label="Utilization" value={s.utilization_rate} suffix="%" icon={BarChart3} color="teal" />
        <TsSummaryCard label="Avg/Day" value={s.avg_hours_per_day} suffix="h" icon={CalendarDays} color="violet" />
      </div>

      {/* Category Breakdown + Idle Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Category Time Distribution */}
        <Card data-testid="ts-category-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChart className="h-4 w-4 text-teal-600" />
              Where Time Is Spent
              <Badge variant="secondary" className="ml-auto">{categories.length} categories</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {categories.length === 0 ? (
              <p className="text-gray-400 text-center py-6 text-sm">No timesheet data</p>
            ) : (
              <>
                {/* Stacked bar */}
                <div className="h-6 bg-gray-100 rounded-full overflow-hidden flex">
                  {categories.map((c, i) => (
                    <div key={c.category}
                      className={`h-full ${CATEGORY_COLORS[i % CATEGORY_COLORS.length].bg} transition-all`}
                      style={{ width: `${c.percentage}%` }}
                      title={`${c.category}: ${c.hours}h (${c.percentage}%)`} />
                  ))}
                </div>
                {/* Legend */}
                <div className="space-y-2">
                  {categories.map((c, i) => {
                    const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
                    return (
                      <div key={c.category} className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-sm shrink-0 ${color.bg}`} />
                        <span className="text-sm text-gray-700 flex-1 truncate">{c.category}</span>
                        <span className="text-sm font-medium text-gray-900 tabular-nums">{c.hours}h</span>
                        <span className="text-xs text-gray-400 w-12 text-right tabular-nums">{c.percentage}%</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Idle / Utilization Insight */}
        <Card data-testid="ts-idle-analysis">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Timer className="h-4 w-4 text-red-500" />
              Utilization & Idle Time
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Gauge-like visual */}
            <div className="flex items-center gap-4 py-2">
              <div className="relative w-24 h-24">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#14b8a6" strokeWidth="3"
                    strokeDasharray={`${(s.utilization_rate || 0)} 100`}
                    strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-gray-900">{s.utilization_rate || 0}%</span>
                </div>
              </div>
              <div className="space-y-2 flex-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Productive</span>
                  <span className="font-medium text-emerald-600">{s.total_hours_logged || 0}h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Unlogged / Idle</span>
                  <span className="font-medium text-red-500">{s.total_hours_idle || 0}h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Target ({s.daily_target_hours || 8}h/day)</span>
                  <span className="font-medium text-gray-700">{s.total_hours_target || 0}h</span>
                </div>
              </div>
            </div>

            {/* Office utilization breakdown */}
            {officeBreakdown.length > 0 && (
              <div className="space-y-2 pt-2 border-t">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">By Office</p>
                {officeBreakdown.map(o => (
                  <div key={o.office_id} className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 flex-1 truncate">{o.office_name || 'Unknown'}</span>
                    <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full" style={{ width: `${Math.min(100, o.utilization)}%` }} />
                    </div>
                    <span className={`text-sm font-medium tabular-nums w-12 text-right ${o.utilization >= 80 ? 'text-emerald-600' : o.utilization >= 60 ? 'text-amber-600' : 'text-red-500'}`}>
                      {o.utilization}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Employee Breakdown */}
      <Card data-testid="ts-employee-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" />
            Employee Time Analysis
            <Badge variant="secondary" className="ml-auto">{employees.length} employees</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {employees.length === 0 ? (
              <p className="text-gray-400 text-center py-10 text-sm">No data for this period</p>
            ) : employees.map((emp, idx) => (
              <div key={emp.user_id} data-testid={`ts-emp-${idx}`}>
                <div className="flex items-center gap-4 px-5 py-3.5 cursor-pointer hover:bg-gray-50/80 transition-colors"
                  onClick={() => setExpandedEmp(prev => prev === emp.user_id ? null : emp.user_id)}>
                  <div className="min-w-[140px]">
                    <p className="font-medium text-sm text-gray-900">{emp.employee_name}</p>
                    <p className="text-xs text-gray-400">{emp.office_name} &middot; {emp.days} day{emp.days !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex-1">
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
                      {emp.categories?.map((c, ci) => (
                        <div key={c.category}
                          className={`h-full ${CATEGORY_COLORS[ci % CATEGORY_COLORS.length].bg}`}
                          style={{ width: `${c.percentage}%` }}
                          title={`${c.category}: ${Math.round(c.minutes / 60 * 10) / 10}h`} />
                      ))}
                    </div>
                  </div>
                  <div className="text-right min-w-[80px]">
                    <p className="font-bold text-sm tabular-nums">{Math.round(emp.total_minutes / 60 * 10) / 10}h</p>
                    <p className="text-xs text-gray-400">avg {Math.round(emp.avg_minutes_per_day / 60 * 10) / 10}h/day</p>
                  </div>
                  <Badge variant="outline" className={`min-w-[50px] justify-center ${
                    emp.utilization >= 80 ? 'text-green-600 border-green-300 bg-green-50'
                      : emp.utilization >= 60 ? 'text-amber-600 border-amber-300 bg-amber-50'
                      : 'text-red-600 border-red-300 bg-red-50'
                  }`}>{emp.utilization}%</Badge>
                  <div className="text-gray-400">
                    {expandedEmp === emp.user_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
                {expandedEmp === emp.user_id && (
                  <div className="px-5 pb-4 bg-gray-50/50 space-y-3">
                    <div className="grid grid-cols-4 gap-3 pt-2">
                      <div className="bg-white rounded-lg border px-3 py-2 text-center">
                        <p className="text-xs text-gray-500">Logged</p>
                        <p className="font-bold text-sm">{Math.round(emp.total_minutes / 60 * 10) / 10}h</p>
                      </div>
                      <div className="bg-white rounded-lg border px-3 py-2 text-center">
                        <p className="text-xs text-gray-500">Idle</p>
                        <p className="font-bold text-sm text-red-500">{Math.round(emp.idle_minutes / 60 * 10) / 10}h</p>
                      </div>
                      <div className="bg-white rounded-lg border px-3 py-2 text-center">
                        <p className="text-xs text-gray-500">Top Category</p>
                        <p className="font-bold text-sm truncate">{emp.top_category}</p>
                      </div>
                      <div className="bg-white rounded-lg border px-3 py-2 text-center">
                        <p className="text-xs text-gray-500">Entries</p>
                        <p className="font-bold text-sm">{emp.entry_count}</p>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      {emp.categories?.map((c, ci) => (
                        <div key={c.category} className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-sm ${CATEGORY_COLORS[ci % CATEGORY_COLORS.length].bg}`} />
                          <span className="text-xs text-gray-600 flex-1">{c.category}</span>
                          <span className="text-xs font-medium tabular-nums">{Math.round(c.minutes / 60 * 10) / 10}h</span>
                          <span className="text-xs text-gray-400 w-10 text-right tabular-nums">{c.percentage}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Individual Timesheet Preview */}
      <Card data-testid="ts-preview-list">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="h-4 w-4 text-violet-600" />
            Submitted Timesheets
            <Badge variant="secondary" className="ml-auto">{timesheets.length} records</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {timesheets.length === 0 ? (
              <p className="text-gray-400 text-center py-10 text-sm">No timesheets for this period</p>
            ) : timesheets.map((ts, idx) => (
              <div key={ts.id || idx} data-testid={`ts-row-${idx}`}>
                <div className="flex items-center gap-4 px-5 py-3 cursor-pointer hover:bg-gray-50/80 transition-colors"
                  onClick={() => setExpandedRow(prev => prev === idx ? null : idx)}>
                  <div className="min-w-[130px]">
                    <p className="text-sm font-medium text-gray-900">{ts.employee_name}</p>
                    <p className="text-xs text-gray-400">{ts.office_name}</p>
                  </div>
                  <div className="text-xs text-gray-500 min-w-[70px]">{formatDate(ts.date)}</div>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="h-2 flex-1 bg-gray-100 rounded-full overflow-hidden flex">
                      {Object.entries(ts.categories || {}).map(([cat, mins], ci) => (
                        <div key={cat}
                          className={`h-full ${CATEGORY_COLORS[ci % CATEGORY_COLORS.length].bg}`}
                          style={{ width: `${ts.total_minutes > 0 ? (mins / ts.total_minutes * 100) : 0}%` }}
                          title={`${cat}: ${Math.round(mins / 60 * 10) / 10}h`} />
                      ))}
                    </div>
                  </div>
                  <div className="text-right min-w-[55px]">
                    <span className="font-medium text-sm tabular-nums">{formatMin(ts.total_minutes)}</span>
                  </div>
                  {ts.idle_minutes > 0 && (
                    <Badge variant="outline" className="text-red-500 border-red-200 text-xs">
                      -{formatMin(ts.idle_minutes)} idle
                    </Badge>
                  )}
                  <Badge variant="outline" className={ts.status === 'Logged' ? 'text-green-600 border-green-300' : 'text-amber-600 border-amber-300'}>
                    {ts.status || 'Draft'}
                  </Badge>
                  <div className="text-gray-400">
                    {expandedRow === idx ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
                {expandedRow === idx && (
                  <div className="px-5 pb-4 bg-gray-50/50">
                    <table className="w-full text-sm mt-2">
                      <thead>
                        <tr className="text-xs text-gray-500 border-b">
                          <th className="text-left py-1.5 font-medium">Task</th>
                          <th className="text-left py-1.5 font-medium">Category</th>
                          <th className="text-right py-1.5 font-medium">Duration</th>
                          <th className="text-left py-1.5 font-medium pl-4">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {ts.entries?.map((e, ei) => (
                          <tr key={ei} className="text-gray-700">
                            <td className="py-2 font-medium">{e.task || '-'}</td>
                            <td className="py-2">
                              <Badge variant="secondary" className="text-xs">{e.category || 'Uncategorized'}</Badge>
                            </td>
                            <td className="py-2 text-right tabular-nums font-medium">{formatMin(e.minutes)}</td>
                            <td className="py-2 pl-4 text-gray-500 max-w-[250px] truncate">{e.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function TsSummaryCard({ label, value, suffix, icon: Icon, color }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-50 text-red-600',
    teal: 'bg-teal-50 text-teal-600',
    violet: 'bg-violet-50 text-violet-600',
  };
  return (
    <Card>
      <CardContent className="py-4 px-4">
        <div className="flex items-center gap-2 mb-2">
          <div className={`p-1.5 rounded-lg ${colorMap[color] || colorMap.blue}`}>
            <Icon className="h-3.5 w-3.5" />
          </div>
          <span className="text-xs text-gray-500">{label}</span>
        </div>
        <p className="text-2xl font-bold text-gray-900 tabular-nums">{value ?? 0}<span className="text-xs font-normal text-gray-400 ml-0.5">{suffix}</span></p>
      </CardContent>
    </Card>
  );
}

// ─── Expenses Tab ────────────────────────────────────────────────────
function ExpensesTab({ data, filterLabel }) {
  const [expandedSection, setExpandedSection] = useState(null);
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;

  const s = data.summary || {};
  const categories = data.category_breakdown || [];
  const topSpenders = data.top_spenders || [];
  const statusBreakdown = data.status_breakdown || {};
  const monthlyTrend = data.monthly_trend || [];
  const officeBreakdown = data.office_breakdown || [];

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700', reviewing: 'bg-blue-100 text-blue-700',
    authorizing: 'bg-purple-100 text-purple-700', approved: 'bg-green-100 text-green-700',
    completed: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700',
    disbursed: 'bg-teal-100 text-teal-700',
  };

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <TsSummaryCard label="Total Requests" value={s.total_requests} icon={FileText} color="blue" />
        <TsSummaryCard label="Total Amount" value={s.total_amount?.toLocaleString()} suffix={` ${s.currency || 'TZS'}`} icon={DollarSign} color="emerald" />
        <TsSummaryCard label="Avg Amount" value={s.avg_amount?.toLocaleString()} suffix={` ${s.currency || 'TZS'}`} icon={TrendingUp} color="violet" />
        <TsSummaryCard label="Approval Rate" value={s.approval_rate} suffix="%" icon={CheckCircle2} color="teal" />
        <TsSummaryCard label="Period" value={filterLabel} icon={Calendar} color="gray" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Category Breakdown */}
        <Card data-testid="expense-category-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChart className="h-4 w-4 text-teal-600" /> Spending by Category
              <Badge variant="secondary" className="ml-auto">{categories.length} categories</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {categories.length === 0 ? (
              <p className="text-gray-400 text-center py-6 text-sm">No expense data</p>
            ) : (
              <>
                <div className="h-6 bg-gray-100 rounded-full overflow-hidden flex">
                  {categories.map((c, i) => (
                    <div key={c.category} className={`h-full ${CATEGORY_COLORS[i % CATEGORY_COLORS.length].bg}`}
                      style={{ width: `${c.percentage}%` }} title={`${c.category}: ${c.amount?.toLocaleString()} (${c.percentage}%)`} />
                  ))}
                </div>
                <div className="space-y-2">
                  {categories.map((c, i) => (
                    <div key={c.category} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-sm shrink-0 ${CATEGORY_COLORS[i % CATEGORY_COLORS.length].bg}`} />
                      <span className="text-sm text-gray-700 flex-1 truncate">{c.category}</span>
                      <span className="text-sm font-medium text-gray-900 tabular-nums">{c.amount?.toLocaleString()}</span>
                      <span className="text-xs text-gray-400 w-10 text-right tabular-nums">{c.percentage}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Status Breakdown */}
        <Card data-testid="expense-status-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" /> Request Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.keys(statusBreakdown).length === 0 ? (
              <p className="text-gray-400 text-center py-6 text-sm">No data</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(statusBreakdown).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                  <div key={status} className="flex items-center gap-3">
                    <Badge className={`text-xs min-w-[90px] justify-center ${statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-700'}`}>
                      {status}
                    </Badge>
                    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full" style={{ width: `${s.total_requests > 0 ? (count / s.total_requests * 100) : 0}%` }} />
                    </div>
                    <span className="text-sm font-medium tabular-nums w-8 text-right">{count}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Monthly Trend */}
            {monthlyTrend.length > 0 && (
              <div className="pt-3 border-t space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Trend</p>
                {monthlyTrend.map(m => (
                  <div key={m.month} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-16">{m.month}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${s.total_amount > 0 ? (m.amount / s.total_amount * 100) : 0}%` }} />
                    </div>
                    <span className="text-xs font-medium tabular-nums">{m.amount?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Spenders */}
      <Card data-testid="expense-top-spenders">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-violet-600" /> Top Spenders
            <Badge variant="secondary" className="ml-auto">{topSpenders.length} employees</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600 text-xs uppercase">#</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600 text-xs uppercase">Employee</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600 text-xs uppercase">Office</th>
                  <th className="px-4 py-2.5 text-right font-medium text-gray-600 text-xs uppercase">Requests</th>
                  <th className="px-4 py-2.5 text-right font-medium text-gray-600 text-xs uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {topSpenders.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                ) : topSpenders.map((sp, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</div>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-gray-900">{sp.employee_name}</td>
                    <td className="px-4 py-2.5 text-gray-500">{sp.office_name}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{sp.count}</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums">{sp.amount?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Office Breakdown */}
      {officeBreakdown.length > 0 && (
        <Card data-testid="expense-office-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="h-4 w-4 text-blue-600" /> Expenses by Office
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {officeBreakdown.map(o => (
              <div key={o.office_id} className="flex items-center gap-3">
                <span className="text-sm text-gray-700 flex-1 truncate">{o.office_name || 'Unknown'}</span>
                <div className="w-32 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${s.total_amount > 0 ? (o.amount / s.total_amount * 100) : 0}%` }} />
                </div>
                <span className="text-sm font-medium tabular-nums w-20 text-right">{o.amount?.toLocaleString()}</span>
                <Badge variant="outline" className="text-xs">{o.count} req</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}

// ─── Leave & Absence Tab ─────────────────────────────────────────────
function LeavesTab({ data, filterLabel }) {
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;

  const s = data.summary || {};
  const typeBreakdown = data.type_breakdown || [];
  const statusBreakdown = data.status_breakdown || {};
  const employeeBreakdown = data.employee_breakdown || [];
  const monthlyPattern = data.monthly_pattern || [];
  const officeBreakdown = data.office_breakdown || [];

  const statusColors = {
    'Approved': 'bg-green-100 text-green-700', 'Pending Manager': 'bg-amber-100 text-amber-700',
    'Pending HR': 'bg-blue-100 text-blue-700', 'Rejected': 'bg-red-100 text-red-700',
    'Cancelled': 'bg-gray-100 text-gray-600',
  };

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <TsSummaryCard label="Total Requests" value={s.total_requests} icon={FileText} color="blue" />
        <TsSummaryCard label="Total Days" value={s.total_days} suffix=" days" icon={Palmtree} color="emerald" />
        <TsSummaryCard label="Avg Duration" value={s.avg_duration} suffix=" days" icon={Clock} color="violet" />
        <TsSummaryCard label="Approval Rate" value={s.approval_rate} suffix="%" icon={CheckCircle2} color="teal" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Leave Type Distribution */}
        <Card data-testid="leave-type-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChart className="h-4 w-4 text-teal-600" /> Leave Type Distribution
              <Badge variant="secondary" className="ml-auto">{typeBreakdown.length} types</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {typeBreakdown.length === 0 ? (
              <p className="text-gray-400 text-center py-6 text-sm">No leave data</p>
            ) : (
              <>
                <div className="h-6 bg-gray-100 rounded-full overflow-hidden flex">
                  {typeBreakdown.map((t, i) => (
                    <div key={t.leave_type} className={`h-full ${CATEGORY_COLORS[i % CATEGORY_COLORS.length].bg}`}
                      style={{ width: `${t.percentage}%` }} title={`${t.leave_type}: ${t.days} days (${t.percentage}%)`} />
                  ))}
                </div>
                <div className="space-y-2">
                  {typeBreakdown.map((t, i) => (
                    <div key={t.leave_type} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-sm shrink-0 ${CATEGORY_COLORS[i % CATEGORY_COLORS.length].bg}`} />
                      <span className="text-sm text-gray-700 flex-1 truncate">{t.leave_type}</span>
                      <span className="text-sm font-medium text-gray-900 tabular-nums">{t.days}d</span>
                      <span className="text-xs text-gray-400 tabular-nums">({t.count} req)</span>
                      <span className="text-xs text-gray-400 w-10 text-right tabular-nums">{t.percentage}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Status & Monthly Pattern */}
        <Card data-testid="leave-status-monthly">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" /> Request Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.keys(statusBreakdown).length === 0 ? (
              <p className="text-gray-400 text-center py-6 text-sm">No data</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(statusBreakdown).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                  <div key={status} className="flex items-center gap-3">
                    <Badge className={`text-xs min-w-[100px] justify-center ${statusColors[status] || 'bg-gray-100 text-gray-700'}`}>
                      {status}
                    </Badge>
                    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full"
                        style={{ width: `${s.total_requests > 0 ? (count / s.total_requests * 100) : 0}%` }} />
                    </div>
                    <span className="text-sm font-medium tabular-nums w-8 text-right">{count}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Monthly Pattern */}
            {monthlyPattern.length > 0 && (
              <div className="pt-3 border-t space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Pattern</p>
                {monthlyPattern.map(m => (
                  <div key={m.month} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-16">{m.month}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full"
                        style={{ width: `${s.total_days > 0 ? (m.days / s.total_days * 100) : 0}%` }} />
                    </div>
                    <span className="text-xs font-medium tabular-nums">{m.days}d ({m.count})</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Employee Leave Breakdown */}
      <Card data-testid="leave-employee-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-violet-600" /> Employee Leave Summary
            <Badge variant="secondary" className="ml-auto">{employeeBreakdown.length} employees</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600 text-xs uppercase">#</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600 text-xs uppercase">Employee</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600 text-xs uppercase">Office</th>
                  <th className="px-4 py-2.5 text-right font-medium text-gray-600 text-xs uppercase">Requests</th>
                  <th className="px-4 py-2.5 text-right font-medium text-gray-600 text-xs uppercase">Days Taken</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {employeeBreakdown.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                ) : employeeBreakdown.slice(0, 20).map((emp, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</div>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-gray-900">{emp.employee_name}</td>
                    <td className="px-4 py-2.5 text-gray-500">{emp.office_name}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{emp.count}</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums">{emp.days} days</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Office Breakdown */}
      {officeBreakdown.length > 0 && (
        <Card data-testid="leave-office-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="h-4 w-4 text-blue-600" /> Leave by Office
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {officeBreakdown.map(o => (
              <div key={o.office_id} className="flex items-center gap-3">
                <span className="text-sm text-gray-700 flex-1 truncate">{o.office_name || 'Unknown'}</span>
                <div className="w-32 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-500 rounded-full"
                    style={{ width: `${s.total_days > 0 ? (o.days / s.total_days * 100) : 0}%` }} />
                </div>
                <span className="text-sm font-medium tabular-nums w-16 text-right">{o.days} days</span>
                <Badge variant="outline" className="text-xs">{o.count} req</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}

// ─── Task Performance Tab ────────────────────────────────────────────
function TasksAnalysisTab({ data, filterLabel }) {
  const [expandedEmp, setExpandedEmp] = useState(null);
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;

  const s = data.summary || {};
  const statusBreakdown = data.status_breakdown || {};
  const priorityBreakdown = data.priority_breakdown || {};
  const typeBreakdown = data.type_breakdown || {};
  const workload = data.workload_breakdown || [];
  const collabs = data.top_collaborations || [];

  const statusColors = {
    'To Do': 'bg-gray-100 text-gray-700', 'In Progress': 'bg-blue-100 text-blue-700',
    'Completed': 'bg-green-100 text-green-700', 'Cancelled': 'bg-red-100 text-red-700',
    'On Hold': 'bg-amber-100 text-amber-700',
  };
  const priorityColors = {
    'Low': 'bg-gray-100 text-gray-600', 'Medium': 'bg-blue-100 text-blue-700',
    'High': 'bg-orange-100 text-orange-700', 'Urgent': 'bg-red-100 text-red-700',
  };

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <TsSummaryCard label="Total Tasks" value={s.total_tasks} icon={ListChecks} color="blue" />
        <TsSummaryCard label="Completed" value={s.completed} icon={CheckCircle2} color="emerald" />
        <TsSummaryCard label="Completion Rate" value={s.completion_rate} suffix="%" icon={Target} color="teal" />
        <TsSummaryCard label="Overdue" value={s.overdue} icon={AlertTriangle} color="red" />
        <TsSummaryCard label="Avg Completion" value={s.avg_completion_days} suffix=" days" icon={Clock} color="violet" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Status Breakdown */}
        <Card data-testid="task-status-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" /> By Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(statusBreakdown).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <Badge className={`text-xs min-w-[80px] justify-center ${statusColors[status] || 'bg-gray-100 text-gray-700'}`}>{status}</Badge>
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${s.total_tasks > 0 ? (count / s.total_tasks * 100) : 0}%` }} />
                </div>
                <span className="text-sm font-medium tabular-nums w-8 text-right">{count}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Priority Breakdown */}
        <Card data-testid="task-priority-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" /> By Priority
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(priorityBreakdown).sort((a, b) => b[1] - a[1]).map(([priority, count]) => (
              <div key={priority} className="flex items-center gap-3">
                <Badge className={`text-xs min-w-[70px] justify-center ${priorityColors[priority] || 'bg-gray-100 text-gray-700'}`}>{priority}</Badge>
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-orange-500 rounded-full" style={{ width: `${s.total_tasks > 0 ? (count / s.total_tasks * 100) : 0}%` }} />
                </div>
                <span className="text-sm font-medium tabular-nums w-8 text-right">{count}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Type Breakdown */}
        <Card data-testid="task-type-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-violet-600" /> By Type
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(typeBreakdown).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
              <div key={type} className="flex items-center gap-3">
                <Badge variant="outline" className="text-xs min-w-[80px] justify-center capitalize">{type}</Badge>
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-500 rounded-full" style={{ width: `${s.total_tasks > 0 ? (count / s.total_tasks * 100) : 0}%` }} />
                </div>
                <span className="text-sm font-medium tabular-nums w-8 text-right">{count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Employee Workload */}
      <Card data-testid="task-workload-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" /> Employee Workload
            <Badge variant="secondary" className="ml-auto">{workload.length} employees</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {workload.length === 0 ? (
              <p className="text-gray-400 text-center py-10 text-sm">No task data</p>
            ) : workload.map((emp, idx) => (
              <div key={emp.user_id || idx} data-testid={`task-workload-${idx}`}>
                <div className="flex items-center gap-4 px-5 py-3.5 cursor-pointer hover:bg-gray-50/80 transition-colors"
                  onClick={() => setExpandedEmp(prev => prev === emp.user_id ? null : emp.user_id)}>
                  <div className="min-w-[140px]">
                    <p className="font-medium text-sm text-gray-900">{emp.name}</p>
                    <p className="text-xs text-gray-400">{emp.office}</p>
                  </div>
                  <div className="flex-1">
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
                      {emp.total > 0 && (
                        <>
                          <div className="h-full bg-green-500" style={{ width: `${(emp.completed / emp.total) * 100}%` }} title={`Completed: ${emp.completed}`} />
                          <div className="h-full bg-blue-500" style={{ width: `${(emp.in_progress / emp.total) * 100}%` }} title={`In Progress: ${emp.in_progress}`} />
                          <div className="h-full bg-red-400" style={{ width: `${(emp.overdue / emp.total) * 100}%` }} title={`Overdue: ${emp.overdue}`} />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right min-w-[60px]">
                    <span className="font-bold text-sm tabular-nums">{emp.total}</span>
                    <span className="text-xs text-gray-400 ml-1">tasks</span>
                  </div>
                  <Badge variant="outline" className={`min-w-[50px] justify-center ${
                    emp.completion_rate >= 80 ? 'text-green-600 border-green-300 bg-green-50'
                      : emp.completion_rate >= 50 ? 'text-amber-600 border-amber-300 bg-amber-50'
                      : 'text-red-600 border-red-300 bg-red-50'
                  }`}>{emp.completion_rate}%</Badge>
                  <div className="text-gray-400">
                    {expandedEmp === emp.user_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
                {expandedEmp === emp.user_id && (
                  <div className="px-5 pb-4 bg-gray-50/50">
                    <div className="grid grid-cols-4 gap-3 pt-2">
                      <div className="bg-white rounded-lg border px-3 py-2 text-center">
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="font-bold text-sm">{emp.total}</p>
                      </div>
                      <div className="bg-white rounded-lg border px-3 py-2 text-center">
                        <p className="text-xs text-gray-500">Completed</p>
                        <p className="font-bold text-sm text-green-600">{emp.completed}</p>
                      </div>
                      <div className="bg-white rounded-lg border px-3 py-2 text-center">
                        <p className="text-xs text-gray-500">In Progress</p>
                        <p className="font-bold text-sm text-blue-600">{emp.in_progress}</p>
                      </div>
                      <div className="bg-white rounded-lg border px-3 py-2 text-center">
                        <p className="text-xs text-gray-500">Overdue</p>
                        <p className="font-bold text-sm text-red-500">{emp.overdue}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Collaborations */}
      {collabs.length > 0 && (
        <Card data-testid="task-top-collaborations">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-pink-500" /> Top Collaborations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {collabs.map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-pink-100 text-pink-700 flex items-center justify-center text-xs font-bold">{i + 1}</div>
                <span className="text-sm text-gray-700">{c.pair?.[0]}</span>
                <span className="text-xs text-gray-400">+</span>
                <span className="text-sm text-gray-700">{c.pair?.[1]}</span>
                <Badge variant="secondary" className="ml-auto">{c.count} tasks</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}

// ─── Security & Login Tab ────────────────────────────────────────────
function SecurityTab({ data, filterLabel, onlineUsers, getAuthHeaders }) {
  const [localOnline, setLocalOnline] = useState(onlineUsers);
  const [refreshing, setRefreshing] = useState(false);

  // Sync when parent data changes
  useEffect(() => { setLocalOnline(onlineUsers); }, [onlineUsers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/online-users`, { headers: getAuthHeaders() });
      if (res.ok) setLocalOnline(await res.json());
    } catch (e) { console.error('Refresh failed', e); }
    setRefreshing(false);
  };

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;

  const login = data.login_summary || {};
  const session = data.session_summary || {};
  const peakHours = data.peak_hours || [];
  const failureReasons = data.failure_reasons || {};
  const suspicious = data.suspicious_accounts || [];
  const onlineList = localOnline?.users || [];
  const onlineCount = localOnline?.online_count || 0;

  // Find max hour for bar scaling
  const maxHourCount = Math.max(1, ...peakHours.map(h => h.count));

  // Time ago helper
  const timeAgo = (isoStr) => {
    if (!isoStr) return '-';
    try {
      const diff = Date.now() - new Date(isoStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'Just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return `${Math.floor(hrs / 24)}d ago`;
    } catch { return '-'; }
  };

  return (
    <>
      {/* Currently Online */}
      <Card data-testid="security-online-users" className="border-green-200 bg-green-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <div className="relative">
              <Users className="h-4 w-4 text-green-600" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            </div>
            Currently Online
            <Badge className="bg-green-100 text-green-700 border-green-300">{onlineCount} {onlineCount === 1 ? 'user' : 'users'}</Badge>
            <button onClick={handleRefresh} disabled={refreshing} data-testid="refresh-online-users"
              className="ml-auto p-1.5 rounded-md hover:bg-green-100 text-green-600 transition-colors disabled:opacity-50"
              title="Refresh">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {onlineList.length === 0 ? (
            <p className="text-gray-400 text-center py-4 text-sm">No active sessions</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {onlineList.map((u, i) => (
                <div key={u.user_id || i} data-testid={`online-user-${i}`}
                  className="flex items-center gap-3 bg-white rounded-lg border border-green-100 px-4 py-3 hover:shadow-sm transition-shadow">
                  <div className="relative shrink-0">
                    <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold">
                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {u.office}{u.designation ? ` · ${u.designation}` : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge variant="outline" className="text-[10px] mb-0.5">{u.device}</Badge>
                    <p className="text-[10px] text-gray-400">{timeAgo(u.last_activity)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <TsSummaryCard label="Login Attempts" value={login.total_attempts} icon={LogIn} color="blue" />
        <TsSummaryCard label="Success Rate" value={login.success_rate} suffix="%" icon={CheckCircle2} color="emerald" />
        <TsSummaryCard label="Failed Logins" value={login.failed} icon={XCircle} color="red" />
        <TsSummaryCard label="Active Sessions" value={session.active_sessions} icon={Activity} color="teal" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <TsSummaryCard label="Total Sessions" value={session.total_sessions} icon={Shield} color="violet" />
        <TsSummaryCard label="Unique Users" value={session.unique_users} icon={Users} color="blue" />
        <TsSummaryCard label="Avg Session" value={session.avg_session_minutes} suffix=" min" icon={Clock} color="emerald" />
        <TsSummaryCard label="Successful" value={login.successful} icon={Lock} color="gray" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Peak Login Hours */}
        <Card data-testid="security-peak-hours">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" /> Login Activity by Hour
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-40">
              {peakHours.map(h => (
                <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full group">
                  <div className="relative w-full flex justify-center">
                    <span className="absolute -top-5 text-[10px] text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity tabular-nums">
                      {h.count}
                    </span>
                    <div className={`w-full max-w-[14px] rounded-t transition-all ${h.count > 0 ? 'bg-teal-500 group-hover:bg-teal-600' : 'bg-gray-100'}`}
                      style={{ height: `${(h.count / maxHourCount) * 100}%`, minHeight: h.count > 0 ? '4px' : '2px' }} />
                  </div>
                  <span className="text-[9px] text-gray-400 mt-1 tabular-nums">{h.hour}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Failure Reasons */}
        <Card data-testid="security-failure-reasons">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" /> Failure Reasons
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.keys(failureReasons).length === 0 ? (
              <div className="flex flex-col items-center py-6">
                <CheckCircle2 className="h-8 w-8 text-green-400 mb-2" />
                <p className="text-gray-400 text-sm">No login failures</p>
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(failureReasons).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                  <div key={reason} className="flex items-center gap-3">
                    <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                    <span className="text-sm text-gray-700 flex-1">{reason}</span>
                    <div className="w-24 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-red-400 rounded-full"
                        style={{ width: `${login.failed > 0 ? (count / login.failed * 100) : 0}%` }} />
                    </div>
                    <span className="text-sm font-medium tabular-nums w-8 text-right">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Suspicious Accounts */}
      {suspicious.length > 0 && (
        <Card data-testid="security-suspicious-accounts" className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <UserX className="h-4 w-4 text-red-500" /> Suspicious Accounts
              <span className="text-xs text-gray-400 font-normal">(3+ failed attempts)</span>
              <Badge variant="destructive" className="ml-auto">{suspicious.length} accounts</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-50 border-y border-red-100">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium text-red-700 text-xs uppercase">Phone</th>
                    <th className="px-4 py-2.5 text-right font-medium text-red-700 text-xs uppercase">Failed Attempts</th>
                    <th className="px-4 py-2.5 text-left font-medium text-red-700 text-xs uppercase">Reasons</th>
                    <th className="px-4 py-2.5 text-left font-medium text-red-700 text-xs uppercase">Last Attempt</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {suspicious.map((acc, i) => (
                    <tr key={i} className="hover:bg-red-50/30">
                      <td className="px-4 py-2.5 font-mono text-gray-900">{acc.phone}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Badge variant="destructive" className="text-xs">{acc.failed_attempts}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">
                        {Object.entries(acc.reasons || {}).map(([r, c]) => `${r} (${c})`).join(', ')}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">
                        {acc.last_attempt ? new Date(acc.last_attempt).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}



// ─── Shared Components ───────────────────────────────────────────────
function StatCard({ icon: Icon, label, count, color, subtitle, onClick, expanded, testId }) {
  const colors = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', ring: 'ring-blue-100' },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'ring-emerald-100' },
    violet: { bg: 'bg-violet-50', icon: 'text-violet-600', ring: 'ring-violet-100' },
  };
  const c = colors[color] || colors.blue;

  return (
    <Card className={`cursor-pointer transition-all hover:shadow-md ${expanded ? `ring-2 ${c.ring} shadow-md` : ''}`}
      onClick={onClick} data-testid={testId}>
      <CardContent className="py-5 px-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{label}</p>
            <p className="text-3xl font-bold mt-1 text-gray-900">{count}</p>
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          </div>
          <div className={`${c.bg} p-3 rounded-xl`}><Icon className={`h-6 w-6 ${c.icon}`} /></div>
        </div>
        <div className="flex items-center justify-end mt-2 text-xs text-gray-400">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span className="ml-1">{expanded ? 'Hide details' : 'View details'}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailTable({ title, color, columns, rows, testId }) {
  const bc = { blue: 'border-t-blue-500', emerald: 'border-t-emerald-500', violet: 'border-t-violet-500' };
  return (
    <Card className={`border-t-4 ${bc[color] || ''}`} data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4" /> {title}
          <Badge variant="secondary" className="ml-auto">{rows.length} records</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-y">
              <tr>
                {columns.map((col, i) => (
                  <th key={i} className="px-4 py-2.5 text-left font-medium text-gray-600 text-xs uppercase tracking-wider">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.length === 0 ? (
                <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-gray-400">No records found</td></tr>
              ) : rows.map((row, ri) => (
                <tr key={ri} className="hover:bg-gray-50/50 transition-colors">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
